/* eslint-disable no-nested-ternary */
/* eslint-disable indent */
import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { FormControl, FormHelperText, Grid, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material'
import { createStyles, makeStyles } from '@mui/styles'

import {
  flowTaskGroups,
  textFieldTypes,
  timeTypes,
  variantTypes,
  calculationMethods,
  contingencyTypes,
  calculationMethodValues
} from '../../../../../../constants'
import { Logger, LoggerTypes } from '../../../../../../utils'
import { FormTitle, iconNames } from '../../../../../../components' // ButtonFx
import { FlowElementBottomButtons } from '../../../FlowElementBottomButtons'
import '../../../common-elements-style.scss'
import './style.scss'

const NONE_VALUE = 'NONE'

const defaultValue = {
  // -- time
  planned_duration: '',
  contingency_duration: '',
  contingency_type: 'USER_DEFINED', // @see /constants/flowActions/contingencyTypes.js
  reference_duration: '',

  // -- design
  weather_data_id: NONE_VALUE,
  hs_design: '',
  wind_speed_design: '',
  current_speed_design: '',
  custom_criteria_design: '',

  // -- operational
  group_name: NONE_VALUE,
  hs_operational: '',
  hs_calculation_method: 'AS_DESIGNED', // @see /constants/flowActions/calculationMethods.js
  hs_calculation_table_id: '',
  wind_speed_operational: '',
  wind_speed_calculation_method: 'AS_DESIGNED', // @see /constants/flowActions/calculationMethods.js
  wind_speed_calculation_table_id: '',
  current_speed_operational: '',
  current_speed_calculation_method: 'AS_DESIGNED', // @see /constants/flowActions/calculationMethods.js
  custom_criteria_operational: '',
  custom_criteria_calculation_method: 'AS_DESIGNED' // @see /constants/flowActions/calculationMethods.js
}

const useStyles = makeStyles((theme) =>
  createStyles({
    headerColor: {
      color: theme.palette.secondary.main,
      opacity: '0.6'
    }
  })
)

export const FlowElementTaskParameterPanel = ({
  currentTaskInstance,
  weather_data,
  onChangeTask,
  onSaveTask,
  alphaFactors,
  errors
}) => {
  const { t } = useTranslation()
  const styleClasses = useStyles()

  // -- default value
  const defaultContingencyType = contingencyTypes.filter((el) => el.id === defaultValue.contingency_type)[0].value
  const defaultHsCalculationMethod = calculationMethods.filter((el) => el.id === defaultValue.hs_calculation_method)[0]
    .value
  const defaultWindSpeedCalculationMethod = calculationMethods.filter(
    (el) => el.id === defaultValue.wind_speed_calculation_method
  )[0].value
  const defaultCurrentSpeedCalculationMethod = calculationMethods.filter(
    (el) => el.id === defaultValue.current_speed_calculation_method
  )[0].value
  const defaultCustomCriteriaCalculationMethod = calculationMethods.filter(
    (el) => el.id === defaultValue.custom_criteria_calculation_method
  )[0].value

  // == MODEL values ==========
  // -- TIME
  const planned_durationValue =
    `${
      typeof currentTaskInstance.task.planned_duration !== 'undefined' &&
      currentTaskInstance.task.planned_duration !== null
        ? currentTaskInstance.task.planned_duration
        : ''
    }` || defaultValue.planned_duration
  const contingency_durationValue =
    `${
      typeof currentTaskInstance.task.contingency_duration !== 'undefined' &&
      currentTaskInstance.task.contingency_duration !== null
        ? currentTaskInstance.task.contingency_duration
        : ''
    }` || defaultValue.contingency_duration
  const contingency_typeValue =
    `${
      typeof currentTaskInstance.task.contingency_type !== 'undefined' &&
      currentTaskInstance.task.contingency_type !== null
        ? currentTaskInstance.task.contingency_type
        : ''
    }` || defaultContingencyType
  const reference_durationValue =
    `${
      typeof currentTaskInstance.task.reference_duration !== 'undefined' &&
      currentTaskInstance.task.reference_duration !== null
        ? currentTaskInstance.task.reference_duration
        : ''
    }` || defaultValue.reference_duration
  // -- DESIGN
  const weather_dataIdValue =
    `${currentTaskInstance?.task?.weather_data_id ? currentTaskInstance.task.weather_data_id : NONE_VALUE}` ||
    defaultValue.weather_data_id
  const hs_designValue =
    `${
      typeof currentTaskInstance.task.hs_design !== 'undefined' && currentTaskInstance.task.hs_design !== null
        ? currentTaskInstance.task.hs_design
        : ''
    }` || defaultValue.hs_design
  const wind_speed_designValue =
    `${
      typeof currentTaskInstance.task.wind_speed_design !== 'undefined' &&
      currentTaskInstance.task.wind_speed_design !== null
        ? currentTaskInstance.task.wind_speed_design
        : ''
    }` || defaultValue.wind_speed_design
  const current_speed_designValue =
    `${
      typeof currentTaskInstance.task.current_speed_design !== 'undefined' &&
      currentTaskInstance.task.current_speed_design !== null
        ? currentTaskInstance.task.current_speed_design
        : ''
    }` || defaultValue.current_speed_design
  const custom_criteria_designValue =
    `${
      typeof currentTaskInstance.task.custom_criteria_design !== 'undefined' &&
      currentTaskInstance.task.custom_criteria_design !== null
        ? currentTaskInstance.task.custom_criteria_design
        : ''
    }` || defaultValue.custom_criteria_design
  // -- OPERATIONAL
  // ---- Group Name
  const group_nameValue =
    `${currentTaskInstance.group_name ? currentTaskInstance.group_name : NONE_VALUE}` || defaultValue.group_name
  // ---- Waves (Hs)
  const hasRootHsOperational =
    currentTaskInstance.group_name &&
    typeof currentTaskInstance.hs_operational !== 'undefined' &&
    currentTaskInstance.hs_operational !== null
  const hs_operationalValue =
    `${
      hasRootHsOperational
        ? currentTaskInstance.hs_operational
        : typeof currentTaskInstance.task.hs_operational !== 'undefined' &&
          currentTaskInstance.task.hs_operational !== null
        ? currentTaskInstance.task.hs_operational
        : ''
    }` || defaultValue.hs_operational
  const hs_calculation_methodValue =
    `${
      typeof currentTaskInstance.task.hs_calculation_method !== 'undefined' &&
      currentTaskInstance.task.hs_calculation_method !== null
        ? currentTaskInstance.task.hs_calculation_method
        : ''
    }` || defaultHsCalculationMethod
  const hs_calculation_table_idValue =
    `${
      typeof currentTaskInstance.task.hs_calculation_table_id !== 'undefined' &&
      currentTaskInstance.task.hs_calculation_table_id !== null
        ? currentTaskInstance.task.hs_calculation_table_id
        : ''
    }` || defaultValue.hs_calculation_table_id
  // ---- Wind
  const hasRootWindSpeedOperational =
    currentTaskInstance.group_name &&
    typeof currentTaskInstance.wind_speed_operational !== 'undefined' &&
    currentTaskInstance.wind_speed_operational !== null
  const wind_speed_operationalValue =
    `${
      hasRootWindSpeedOperational
        ? currentTaskInstance.wind_speed_operational
        : typeof currentTaskInstance.task.wind_speed_operational !== 'undefined' &&
          currentTaskInstance.task.wind_speed_operational !== null
        ? currentTaskInstance.task.wind_speed_operational
        : ''
    }` || defaultValue.wind_speed_operational
  const wind_speed_calculation_methodValue =
    `${
      typeof currentTaskInstance.task.wind_speed_calculation_method !== 'undefined' &&
      currentTaskInstance.task.wind_speed_calculation_method !== null
        ? currentTaskInstance.task.wind_speed_calculation_method
        : ''
    }` || defaultWindSpeedCalculationMethod
  const wind_speed_calculation_table_idValue =
    `${
      typeof currentTaskInstance.task.wind_speed_calculation_table_id !== 'undefined' &&
      currentTaskInstance.task.wind_speed_calculation_table_id !== null
        ? currentTaskInstance.task.wind_speed_calculation_table_id
        : ''
    }` || defaultValue.wind_speed_calculation_table_id
  // ---- Current Speed
  const current_speed_operationalValue =
    `${
      typeof currentTaskInstance.task.current_speed_operational !== 'undefined' &&
      currentTaskInstance.task.current_speed_operational !== null
        ? currentTaskInstance.task.current_speed_operational
        : ''
    }` || defaultValue.current_speed_operational
  const current_speed_calculation_methodValue =
    `${
      typeof currentTaskInstance.task.current_speed_calculation_method !== 'undefined' &&
      currentTaskInstance.task.current_speed_calculation_method !== null
        ? currentTaskInstance.task.current_speed_calculation_method
        : ''
    }` || defaultCurrentSpeedCalculationMethod
  // ---- Custom Criteria
  const custom_criteria_operationalValue =
    `${
      typeof currentTaskInstance.task.custom_criteria_operational !== 'undefined' &&
      currentTaskInstance.task.custom_criteria_operational !== null
        ? currentTaskInstance.task.custom_criteria_operational
        : ''
    }` || defaultValue.custom_criteria_operational
  const custom_criteria_calculation_methodValue =
    `${
      typeof currentTaskInstance.task.custom_criteria_calculation_method !== 'undefined' &&
      currentTaskInstance.task.custom_criteria_calculation_method !== null
        ? currentTaskInstance.task.custom_criteria_calculation_method
        : ''
    }` || defaultCustomCriteriaCalculationMethod
  // ===========================

  // == STATE setters ==========
  // -- TIME
  const [planned_duration, setPlannedDuration] = useState(planned_durationValue)
  const [contingency_duration, setContingencyDuration] = useState(contingency_durationValue)
  // const [openContingencyDurationFunctionDialog, setOpenContingencyDurationFunctionDialog] = useState(false)
  const [contingency_type, setContingencyType] = useState(contingency_typeValue)
  const [reference_duration, setReferenceDuration] = useState(reference_durationValue)
  // -- DESIGN
  const [_weather_data_id, setWeatherData] = useState(weather_dataIdValue)
  const [hs_design, setHsDesign] = useState(hs_designValue)
  const [wind_speed_design, setWindSpeedDesign] = useState(wind_speed_designValue)
  const [current_speed_design, setCurrentSpeedDesign] = useState(current_speed_designValue)
  const [custom_criteria_design, setCustomCriteriaDesign] = useState(custom_criteria_designValue)
  // -- OPERATIONAL
  // -- Group Name
  const [group_name, setGroupName] = useState(group_nameValue)
  // ---- Waves (Hs)
  const [hs_operational, setHsOperational] = useState(hs_operationalValue)
  const [hs_calculation_method, setHsCalculationMethod] = useState(hs_calculation_methodValue)
  const [hs_calculation_table_id, setHsCalculationTableId] = useState(hs_calculation_table_idValue)
  // ---- Wind
  const [wind_speed_operational, setWindSpeedOperational] = useState(wind_speed_operationalValue)
  const [wind_speed_calculation_method, setWindSpeedDesignMethod] = useState(wind_speed_calculation_methodValue)
  const [wind_speed_calculation_table_id, setWindSpeedCalculationTableId] = useState(
    wind_speed_calculation_table_idValue
  )
  // ---- Current Speed
  const [current_speed_operational, setCurrentSpeedOperational] = useState(current_speed_operationalValue)
  const [current_speed_calculation_method, setCurrentSpeedCalculationMethod] = useState(
    current_speed_calculation_methodValue
  )
  // ---- Custom Criteria
  const [custom_criteria_operational, setCustomCriteriaOperational] = useState(custom_criteria_operationalValue)
  const [custom_criteria_calculation_method, setCustomCriteriaCalculationMethod] = useState(
    custom_criteria_calculation_methodValue
  )
  // ===========================

  const _getNewTask = () => {
    currentTaskInstance.group_name = group_name
    const newTask = {
      ...currentTaskInstance,
      group_name: group_name === '' ? null : group_name,
      task: {
        ...currentTaskInstance.task,
        // -- time
        planned_duration: planned_duration === '' ? null : planned_duration,
        contingency_duration: contingency_duration === '' ? null : contingency_duration,
        contingency_type: contingency_type === '' ? null : contingency_type,
        // reference_duration: null,

        // -- design
        weather_data_id: _weather_data_id === '' ? null : _weather_data_id,
        hs_design: hs_design === '' ? null : hs_design,
        wind_speed_design: wind_speed_design === '' ? null : wind_speed_design,
        current_speed_design: current_speed_design === '' ? null : current_speed_design,
        custom_criteria_design: custom_criteria_design === '' ? null : custom_criteria_design,

        // -- operational
        // ---- Wave (Hs)
        hs_operational: hs_operational === '' ? null : hs_operational,
        hs_calculation_method: hs_calculation_method === '' ? null : hs_calculation_method,
        hs_calculation_table_id: hs_calculation_table_id === '' ? null : hs_calculation_table_id,
        // ---- Wind
        wind_speed_operational: wind_speed_operational === '' ? null : wind_speed_operational,
        wind_speed_calculation_method: wind_speed_calculation_method === '' ? null : wind_speed_calculation_method,
        wind_speed_calculation_table_id:
          wind_speed_calculation_table_id === '' ? null : wind_speed_calculation_table_id,
        // ---- Current Speed
        current_speed_operational: current_speed_operational === '' ? null : current_speed_operational,
        current_speed_calculation_method:
          current_speed_calculation_method === '' ? null : current_speed_calculation_method,

        // ---- Custom Criteria
        custom_criteria_operational: custom_criteria_operational === '' ? null : custom_criteria_operational,
        custom_criteria_calculation_method:
          custom_criteria_calculation_method === '' ? null : custom_criteria_calculation_method
      }
    }

    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskParameterPanel._getNewTask()', { newTask })

    return newTask
  }

  useEffect(() => {
    // -- TIME
    setPlannedDuration(planned_durationValue)
    setContingencyDuration(contingency_durationValue)
    setContingencyType(contingency_typeValue)
    setReferenceDuration(reference_durationValue)
    // -- DESIGN
    setWeatherData(weather_dataIdValue)
    setHsDesign(hs_designValue)
    setWindSpeedDesign(wind_speed_designValue)
    setCurrentSpeedDesign(current_speed_designValue)
    setCustomCriteriaDesign(custom_criteria_designValue)
    // -- OPERATIONAL
    // ---- Group Name
    setGroupName(group_nameValue)
    // ---- Waves (Hs)
    if (hs_calculation_method === calculationMethodValues.USER_DEFINED) {
      setHsOperational(hs_operational)
    } else {
      setHsOperational(hs_operationalValue)
    }
    setHsCalculationMethod(hs_calculation_methodValue)
    setHsCalculationTableId(hs_calculation_table_idValue)
    // ---- Wind
    if (wind_speed_calculation_method === calculationMethodValues.USER_DEFINED) {
      setWindSpeedOperational(wind_speed_operational)
    } else {
      setWindSpeedOperational(wind_speed_operationalValue)
    }
    setWindSpeedDesignMethod(wind_speed_calculation_methodValue)
    setWindSpeedCalculationTableId(wind_speed_calculation_table_idValue)
    // ---- Current Speed
    setCurrentSpeedOperational(current_speed_operationalValue)
    setCurrentSpeedCalculationMethod(current_speed_calculation_methodValue)
    // ---- Custom Criteria
    setCustomCriteriaOperational(custom_criteria_operationalValue)
    setCustomCriteriaCalculationMethod(custom_criteria_calculation_methodValue)
  }, [currentTaskInstance])

  useEffect(() => {
    const newTask = _getNewTask()
    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskParameterPanel.useEffect() CHANGED', { newTask })
    onChangeTask(newTask)
  }, [
    // -- TIME
    planned_duration,
    contingency_duration,
    contingency_type,
    reference_duration,
    // -- DESIGN
    _weather_data_id,
    hs_design,
    wind_speed_design,
    current_speed_design,
    custom_criteria_design,
    // -- OPERATIONAL
    group_name,
    // ---- Waves (Hs)
    hs_operational,
    hs_calculation_method,
    hs_calculation_table_id,
    // ---- Wind
    wind_speed_operational,
    wind_speed_calculation_method,
    wind_speed_calculation_table_id,
    // ---- Current Speed
    current_speed_operational,
    current_speed_calculation_method,
    // ---- Custom Criteria
    custom_criteria_operational,
    custom_criteria_calculation_method
  ])

  // ==== TIME ====
  // -- Planned Duration
  const onChangePlannedDuration = (e) => {
    e.stopPropagation()
    const _value = e.target.value
    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskParameterPanel.onChangePlannedDuration()', {
      planned_duration: _value
    })

    setPlannedDuration(_value)
  }
  // const onPlannedDurationFunction = (e) => {
  //   e.stopPropagation()
  //   Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskParameterPanel.onPlannedDurationFunction()', {})
  // }

  // -- Contingency Duration / Type
  const onChangeContingencyDuration = (e) => {
    e.stopPropagation()
    const _value = e.target.value
    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskParameterPanel.onChangeContingencyDuration()', {
      contingency_duration: _value
    })
    setContingencyDuration(_value)
  }
  // const onContingencyDurationFunction = (e) => {
  //   e.stopPropagation()
  //   Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskParameterPanel.onContingencyDurationFunction()')
  //   setOpenContingencyDurationFunctionDialog(true)
  // }
  const onChangeContingencyType = (e) => {
    e.stopPropagation()
    const _value = e.target.value
    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskParameterPanel.onChangeContingencyType()', {
      contingency_type: _value
    })
    setContingencyType(_value)
  }

  // ==== DESIGN ====
  // -- Weather Dataset
  const onChangeWeatherData = (e) => {
    e.stopPropagation()
    const _value = e.target.value
    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskGeographyPanel.onChangeWeatherData()', {
      weather_data_id: _value
    })
    setWeatherData(_value)
  }

  // -- HS Design
  const onChangeHsDesign = (e) => {
    e.stopPropagation()
    const _value = e.target.value
    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskParameterPanel.onChangeHsDesign()', {
      hs_design: _value
    })
    setHsDesign(_value)
  }

  // -- wind_speed_design
  const onChangeWindSpeedDesign = (e) => {
    e.stopPropagation()
    const _value = e.target.value
    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskParameterPanel.onChangeWindSpeedDesign()', {
      wind_speed_design: _value
    })
    setWindSpeedDesign(_value)
  }

  // -- current_speed_design
  const onChangeCurrentSpeedDesign = (e) => {
    e.stopPropagation()
    const _value = e.target.value
    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskParameterPanel.onChangeCurrentSpeedDesign()', {
      current_speed_design: _value
    })
    setCurrentSpeedDesign(_value)
  }

  // -- custom_criteria_design
  const onChangeCustomCriteriaDesign = (e) => {
    e.stopPropagation()
    const _value = e.target.value
    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskParameterPanel.onChangeCustomCriteriaDesign()', {
      custom_criteria: _value
    })
    setCustomCriteriaDesign(_value)
  }

  // ==== OPERATIONAL ====

  // -- group_name
  const onChangeGroupName = (e) => {
    e.stopPropagation()
    const _value = e.target.value
    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskAboutPanel.onChangeGroupName()', { group_name: _value })
    setGroupName(_value)
  }

  // -- hs_operational  /  HS Calculation Method
  const onChangeHsOperational = (e) => {
    e.stopPropagation()
    const _value = e.target.value
    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskParameterPanel.onChangeHsOperational()', {
      hs_operational: _value
    })
    setHsOperational(_value)
  }
  const onChangeHsCalculationMethod = (e) => {
    e.stopPropagation()
    const _value = e.target.value
    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskParameterPanel.onChangeHsCalculationMethod()', {
      hs_calculation_method: _value
    })
    setHsCalculationMethod(_value)
  }
  const onChangeHsCalculationTableId = (e) => {
    e.stopPropagation()
    const _value = e.target.value
    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskParameterPanel.onChangeHsCalculationTableId()', {
      hs_calculation_table_id: _value
    })
    setHsCalculationTableId(_value)
  }

  // -- wind_speed_operational  /  wind_speed_calculation_method
  const onChangeWindSpeedOperational = (e) => {
    e.stopPropagation()
    const _value = e.target.value
    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskParameterPanel.onChangeWindSpeedOperational()', {
      wind_speed_operational: _value
    })
    setWindSpeedOperational(_value)
  }
  const onChangeWindSpeedCalculationMethod = (e) => {
    e.stopPropagation()
    const _value = e.target.value
    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskParameterPanel.onChangeWindSpeedCalculationMethod()', {
      wind_speed_calculation_method: _value
    })
    setWindSpeedDesignMethod(_value)
  }
  const onChangeWindSpeedCalculationTableId = (e) => {
    e.stopPropagation()
    const _value = e.target.value
    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskParameterPanel.onChangeWindSpeedCalculationTableId()', {
      wind_speed_calculation_table_id: _value
    })
    setWindSpeedCalculationTableId(_value)
  }

  // -- current_speed_operational  /  current_speed_calculation_method
  const onChangeCurrentSpeedOperational = (e) => {
    e.stopPropagation()
    const _value = e.target.value
    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskParameterPanel.onChangeCurrentSpeedOperational()', {
      current_speed_operational: _value
    })
    setCurrentSpeedOperational(_value)
  }
  const onChangeCurrentSpeedCalculationMethod = (e) => {
    e.stopPropagation()
    const _value = e.target.value
    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskParameterPanel.onChangeCurrentSpeedCalculationMethod()', {
      current_speed_calculation_method: _value
    })
    setCurrentSpeedCalculationMethod(_value)
  }

  // -- custom_criteria_operational  /  custom_criteria_calculation_method
  const onChangeCustomCriteriaOperational = (e) => {
    e.stopPropagation()
    const _value = e.target.value
    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskParameterPanel.onChangeCustomCriteriaOperational()', {
      custom_criteria_operational: _value
    })
    setCustomCriteriaOperational(_value)
  }
  const onChangeCustomCriteriaCalculationMethod = (e) => {
    e.stopPropagation()
    const _value = e.target.value
    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskParameterPanel.onChangeCustomCriteriaCalculationMethod()', {
      custom_criteria_calculation_method: _value
    })
    setCustomCriteriaCalculationMethod(_value)
  }

  // ---

  const onClickSave = (e) => {
    e.stopPropagation()

    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskParameterPanel.onClickSave()')

    onSaveTask((res) => {
      Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskParameterPanel.onClickSave() DONE', { res })
    })
  }

  Logger.trace(LoggerTypes.CONTAINER, 'FlowElementTaskParameterPanel.render()', {
    currentTaskInstance,
    alphaFactors,
    weather_data,
    task: {
      // -- time
      planned_duration,
      contingency_duration,
      contingency_type,
      reference_duration,
      // -- design
      _weather_data_id,
      hs_design,
      wind_speed_design,
      current_speed_design,
      custom_criteria_design,
      // -- operational
      group_name,
      // ---- Wave (Hs)
      hs_operational,
      hs_calculation_method,
      hs_calculation_table_id,
      // ---- Wind
      wind_speed_operational,
      wind_speed_calculation_method,
      wind_speed_calculation_table_id,
      // ---- Speed
      current_speed_operational,
      current_speed_calculation_method,
      // ---- Custom Criteria
      custom_criteria_operational,
      custom_criteria_calculation_method
    }
  })

  const UNIT_MENU_WIDTH = '119px'

  return (
    <div className="FlowElementTaskParameterPanel">
      <form>
        {/* == TIME == */}
        <FormTitle
          className={`parameterGroup ${styleClasses.headerColor}`}
          title={t('flowActions.tasks.panels.parameters.time.title')}
          icon={iconNames.CLOCK}
          iconSize={16}
        />
        <Grid container spacing={2} style={{ padding: '10px' }}>
          {/*  planned_duration  */}
          <Grid item xs={12} sm={12} style={{ position: 'relative' }}>
            <TextField
              type={textFieldTypes.NUMBER}
              name="planned_duration"
              id="planned_duration"
              value={planned_duration}
              variant={variantTypes.OUTLINED}
              label={t('flowActions.tasks.panels.parameters.time.planned_duration.label')}
              className="common_halfwidth"
              InputLabelProps={{ shrink: true }}
              onChange={onChangePlannedDuration}
              placeholder={t('flowActions.tasks.panels.parameters.time.planned_duration.placeholder')}
              error={Boolean(errors && errors?.planned_duration)}
              helperText={(errors && errors?.planned_duration) || ''}
            />
            {/* <ButtonFx
              currentProject={currentProject}
              currentScenario={currentScenario}
              onClick={onPlannedDurationFunction}
              variables={variables}
              openDialog={openContingencyDurationFunctionDialog}
              setOpenDialog={setOpenContingencyDurationFunctionDialog}
            /> */}
            <span className="unit">{timeTypes.HOURS}</span>
          </Grid>

          {/*  contingency_duration  /  contingency_type  */}
          <Grid item xs={12} sm={12} style={{ position: 'relative' }}>
            {/*  contingency_duration  */}
            <TextField
              type={textFieldTypes.NUMBER}
              name="contingency_duration"
              id="contingency_duration"
              value={contingency_duration}
              variant={variantTypes.OUTLINED}
              label={t('flowActions.tasks.panels.parameters.time.contingency_duration.label')}
              className="common_halfwidth"
              InputLabelProps={{ shrink: true }}
              onChange={onChangeContingencyDuration}
              placeholder={t('flowActions.tasks.panels.parameters.time.contingency_duration.placeholder')}
              error={Boolean(errors && errors?.contingency_duration)}
              helperText={(errors && errors?.contingency_duration) || ''}
            />
            {/* <ButtonFx
              currentProject={currentProject}
              currentScenario={currentScenario}
              onClick={onContingencyDurationFunction}
              variables={variables}
              openDialog={openContingencyDurationFunctionDialog}
              setOpenDialog={setOpenContingencyDurationFunctionDialog}
            /> */}
            {/*  contingency_type  */}
            <FormControl error={Boolean(errors && errors?.contingency_type)} style={{ marginLeft: '10px' }}>
              <InputLabel style={{ backgroundColor: 'white', padding: '0 5px' }}>
                {t('flowActions.tasks.panels.parameters.time.contingency_type.label')}
              </InputLabel>
              <Select
                style={{ width: UNIT_MENU_WIDTH }}
                value={String(contingency_type).toUpperCase()}
                defaultValue={defaultContingencyType}
                onChange={onChangeContingencyType}
                variant={variantTypes.OUTLINED}
              >
                {contingencyTypes.map((contingency, index) => (
                  <MenuItem key={`contingencyType-${index}`} value={String(contingency.id).toUpperCase()}>
                    {t(
                      `flowActions.tasks.panels.parameters.time.contingency_type.${String(
                        contingency.id
                      ).toLocaleLowerCase()}`
                    )}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{(errors && errors?.contingency_type) || ''}</FormHelperText>
            </FormControl>
          </Grid>

          {/*  reference_duration  */}
          <Grid item xs={12} sm={12} style={{ position: 'relative' }}>
            <TextField
              disabled={true}
              type={textFieldTypes.NUMBER}
              name="reference_duration"
              id="reference_duration"
              value={reference_duration}
              variant={variantTypes.OUTLINED}
              label={t('flowActions.tasks.panels.parameters.time.reference_duration.label')}
              className="common_halfwidth"
              InputLabelProps={{ shrink: true }}
              placeholder={t('flowActions.tasks.panels.parameters.time.reference_duration.placeholder')}
              error={Boolean(errors && errors?.reference_duration)}
              helperText={(errors && errors?.reference_duration) || ''}
            />
            <span className="unit">{timeTypes.HOURS}</span>
          </Grid>
        </Grid>

        {/* == DESIGN == */}
        <FormTitle
          className={`parameterGroup ${styleClasses.headerColor}`}
          title={t('flowActions.tasks.panels.parameters.design.title')}
          icon={iconNames.PUZZLE}
          iconSize={16}
        />
        <Grid container spacing={2} style={{ padding: '10px' }}>
          {/*  weather_data  */}
          <Grid
            item
            xs={12}
            sm={12}
            className="common_fullwidth"
            style={{ position: 'relative', marginBottom: '25px' }}
          >
            <Typography style={{ height: '25px' }}>
              {t('flowActions.tasks.panels.parameters.design.weather_data.title')}
            </Typography>
            <FormControl error={Boolean(errors && errors?.weather_data_id)} className="common_fullwidth">
              <Select
                className="common_fullwidth"
                value={_weather_data_id || NONE_VALUE}
                onChange={onChangeWeatherData}
                variant={variantTypes.OUTLINED}
                id="weather_data-select"
              >
                <MenuItem value={NONE_VALUE}>
                  {t('flowActions.tasks.panels.parameters.design.weather_data.default')}
                </MenuItem>
                {weather_data.map((weather, index) => (
                  <MenuItem key={`weather_data-start-${index}`} value={weather.id}>
                    {weather.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{(errors && errors?.weather_data_id) || ''}</FormHelperText>
            </FormControl>
          </Grid>

          {/*  WAVE:  hs_design  */}
          <Grid item xs={12} sm={12} style={{ position: 'relative' }}>
            <TextField
              type={textFieldTypes.NUMBER}
              name="hs_design"
              id="hs_design"
              value={hs_design}
              variant={variantTypes.OUTLINED}
              label={t('flowActions.tasks.panels.parameters.design.hs_design.label')}
              className="common_halfwidth"
              InputLabelProps={{ shrink: true }}
              onChange={onChangeHsDesign}
              placeholder={t('flowActions.tasks.panels.parameters.design.hs_design.placeholder')}
              error={Boolean(errors && errors?.hs_design)}
              helperText={(errors && errors?.hs_design) || ''}
            />
            <span className="unit">{t('flowActions.tasks.panels.parameters.operational.hs_operational.unit')}</span>
          </Grid>

          {/*  WIND:  wind_speed_design  */}
          <Grid item xs={12} sm={12} style={{ position: 'relative' }}>
            <TextField
              type={textFieldTypes.NUMBER}
              name="wind_speed_design"
              id="wind_speed_design"
              value={wind_speed_design}
              variant={variantTypes.OUTLINED}
              label={t('flowActions.tasks.panels.parameters.design.wind_speed_design.label')}
              className="common_halfwidth"
              InputLabelProps={{ shrink: true }}
              onChange={onChangeWindSpeedDesign}
              placeholder={t('flowActions.tasks.panels.parameters.design.wind_speed_design.placeholder')}
              error={Boolean(errors && errors?.wind_speed_design)}
              helperText={(errors && errors?.wind_speed_design) || ''}
            />
            <span className="unit">
              {t('flowActions.tasks.panels.parameters.operational.wind_speed_operational.unit')}
            </span>
          </Grid>

          {/*  CURRENT SPEED:  current_speed_design  */}
          <Grid item xs={12} sm={12} style={{ position: 'relative' }}>
            <TextField
              type={textFieldTypes.NUMBER}
              name="current_speed_design"
              id="current_speed_design"
              value={current_speed_design}
              variant={variantTypes.OUTLINED}
              label={t('flowActions.tasks.panels.parameters.design.current_speed_design.label')}
              className="common_halfwidth"
              InputLabelProps={{ shrink: true }}
              onChange={onChangeCurrentSpeedDesign}
              placeholder={t('flowActions.tasks.panels.parameters.design.current_speed_design.placeholder')}
              error={Boolean(errors && errors?.current_speed_design)}
              helperText={(errors && errors?.current_speed_design) || ''}
            />
            <span className="unit">
              {t('flowActions.tasks.panels.parameters.operational.current_speed_operational.unit')}
            </span>
          </Grid>

          {/*  CUSTOM CRITERIA:  custom_criteria_design  */}
          <Grid item xs={12} sm={12} style={{ position: 'relative' }}>
            <TextField
              type={textFieldTypes.NUMBER}
              name="custom_criteria_design"
              id="custom_criteria_design"
              value={custom_criteria_design}
              variant={variantTypes.OUTLINED}
              label={t('flowActions.tasks.panels.parameters.design.custom_criteria_design.label')}
              className="common_halfwidth"
              InputLabelProps={{ shrink: true }}
              onChange={onChangeCustomCriteriaDesign}
              placeholder={t('flowActions.tasks.panels.parameters.design.custom_criteria_design.placeholder')}
              error={Boolean(errors && errors?.custom_criteria_design)}
              helperText={(errors && errors?.custom_criteria_design) || ''}
            />
          </Grid>
        </Grid>

        {/* == OPERATIONAL == */}
        <FormTitle
          className={`parameterGroup ${styleClasses.headerColor}`}
          title={t('flowActions.tasks.panels.parameters.operational.title')}
          icon={iconNames.OPERATIONAL}
          iconSize={16}
        />
        <Grid container spacing={2} style={{ padding: '10px' }}>
          {/*  group_name */}
          <Grid item xs={12} sm={12} className="common_fullwidth" style={{ marginBottom: '25px' }}>
            <Typography style={{ height: '25px' }}>{t('flowActions.tasks.panels.about.group_name.header')}</Typography>
            <FormControl error={Boolean(errors && errors?.group_name)} className="common_fullwidth">
              <Select
                className="common_fullwidth"
                value={group_name || NONE_VALUE}
                onChange={onChangeGroupName}
                variant={variantTypes.OUTLINED}
                id="group-name-select"
              >
                <MenuItem value={NONE_VALUE}>{t('flowActions.tasks.panels.about.group_name.default')}</MenuItem>
                {flowTaskGroups.map((taskGroup, index) => (
                  <MenuItem key={`taskGroup-${index}`} value={taskGroup.value}>
                    <span
                      style={{
                        borderRadius: '100%',
                        width: '14px',
                        height: '14px',
                        backgroundColor: taskGroup.color,
                        marginRight: '10px'
                      }}
                    ></span>
                    <span>{t(`flowActions.tasks.panels.about.group_name.${taskGroup.id}`)}</span>
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{(errors && errors?.group_name) || ''}</FormHelperText>
            </FormControl>
          </Grid>

          {/* WAVE: hs_operational  /  hs_calculation_method  */}
          <Grid item xs={12} sm={12} style={{ position: 'relative' }}>
            <TextField
              disabled={
                hs_calculation_method === calculationMethodValues.AS_DESIGNED ||
                hs_calculation_method === calculationMethodValues.DNV_ST_N001
              }
              type={textFieldTypes.NUMBER}
              name="hs_operational"
              id="hs_operational"
              value={hs_operational}
              variant={variantTypes.OUTLINED}
              label={t('flowActions.tasks.panels.parameters.operational.hs_operational.label')}
              className="common_halfwidth"
              InputLabelProps={{ shrink: true }}
              onChange={onChangeHsOperational}
              placeholder={t('flowActions.tasks.panels.parameters.operational.hs_operational.placeholder')}
              error={Boolean(errors && errors?.hs_operational)}
              helperText={(errors && errors?.hs_operational) || ''}
            />
            <FormControl error={Boolean(errors && errors?.hs_calculation_method)} style={{ marginLeft: '10px' }}>
              <InputLabel style={{ backgroundColor: 'white', padding: '0 5px' }}>
                {t('flowActions.tasks.panels.parameters.operational.hs_calculation_method.label')}
              </InputLabel>
              <Select
                style={{ width: UNIT_MENU_WIDTH }}
                value={hs_calculation_method}
                defaultValue={defaultHsCalculationMethod}
                onChange={onChangeHsCalculationMethod}
                variant={variantTypes.OUTLINED}
              >
                {calculationMethods.map((calcul, index) => (
                  <MenuItem key={`wave-calculationMethod-${index}`} value={calcul.value}>
                    {t(
                      `flowActions.tasks.panels.parameters.operational.calculation_method.${String(
                        calcul.id
                      ).toLocaleLowerCase()}`
                    )}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{(errors && errors?.hs_calculation_method) || ''}</FormHelperText>
            </FormControl>
            {/* {hasRootHsOperational ? (
              <p className="instanceOperational">
                {t('flowActions.tasks.panels.parameters.operational.hs_operational.instance_value')}
                <b>{currentTaskInstance.task.hs_operational}</b>
                {t('flowActions.tasks.panels.parameters.operational.hs_operational.label')}
              </p>
            ) : null} */}
          </Grid>

          {/*  WAVE: hs_calculation_table_id  /  current_speed_calculation_method  */}
          {
            // -- display only if the previous field: hs_calculation_method === 'DNV-ST-N001'
            hs_calculation_method === calculationMethods[2].value && (
              <Grid item xs={12} sm={12} style={{ position: 'relative' }} className="common_fullwidth">
                <FormControl error={Boolean(errors && errors?.hs_calculation_table_id)} className="common_fullwidth">
                  <InputLabel style={{ backgroundColor: 'white', padding: '0 5px' }}>
                    {t('flowActions.tasks.panels.parameters.operational.hs_calculation_table_id.label')}
                  </InputLabel>
                  <Select
                    className="common_fullwidth"
                    value={hs_calculation_table_id}
                    onChange={onChangeHsCalculationTableId}
                    variant={variantTypes.OUTLINED}
                  >
                    {alphaFactors.waves.map((wave, index) => (
                      <MenuItem key={`waveFactor-${index}`} value={wave.id}>
                        {wave.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{(errors && errors?.hs_calculation_table_id) || ''}</FormHelperText>
                </FormControl>
              </Grid>
            )
          }

          {/* WIND: wind_speed_operational  /  wind_speed_calculation_method  */}
          <Grid item xs={12} sm={12} style={{ position: 'relative' }}>
            <TextField
              disabled={
                wind_speed_calculation_method === calculationMethodValues.AS_DESIGNED ||
                wind_speed_calculation_method === calculationMethodValues.DNV_ST_N001
              }
              type={textFieldTypes.NUMBER}
              name="wind_speed_operational"
              id="wind_speed_operational"
              value={wind_speed_operational}
              variant={variantTypes.OUTLINED}
              label={t('flowActions.tasks.panels.parameters.operational.wind_speed_operational.label')}
              className="common_halfwidth"
              InputLabelProps={{ shrink: true }}
              onChange={onChangeWindSpeedOperational}
              placeholder={t('flowActions.tasks.panels.parameters.operational.wind_speed_operational.placeholder')}
              error={Boolean(errors && errors?.wind_speed_operational)}
              helperText={(errors && errors?.wind_speed_operational) || ''}
            />
            <FormControl
              error={Boolean(errors && errors?.wind_speed_calculation_method)}
              style={{ marginLeft: '10px' }}
            >
              <InputLabel style={{ backgroundColor: 'white', padding: '0 5px' }}>
                {t('flowActions.tasks.panels.parameters.operational.wind_speed_calculation_method.label')}
              </InputLabel>
              <Select
                style={{ width: UNIT_MENU_WIDTH }}
                value={wind_speed_calculation_method}
                defaultValue={defaultWindSpeedCalculationMethod}
                onChange={onChangeWindSpeedCalculationMethod}
                variant={variantTypes.OUTLINED}
              >
                {calculationMethods.map((calcul, index) => (
                  <MenuItem key={`wind-calculationMethod-${index}`} value={calcul.value}>
                    {t(
                      `flowActions.tasks.panels.parameters.operational.calculation_method.${String(
                        calcul.id
                      ).toLocaleLowerCase()}`
                    )}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{(errors && errors?.wind_speed_calculation_method) || ''}</FormHelperText>
            </FormControl>
            {/* {hasRootWindSpeedOperational ? (
              <p className="instanceOperational">
                {t('flowActions.tasks.panels.parameters.operational.wind_speed_operational.instance_value')}
                <b>{currentTaskInstance.task.wind_speed_operational}</b>
                {t('flowActions.tasks.panels.parameters.operational.wind_speed_operational.label')}
              </p>
            ) : null} */}
          </Grid>

          {/*  WIND:  wind_speed_calculation_table_id  */}
          {
            // -- display only if the previous field: wind_speed_calculation_method === 'DNV-ST-N001'
            wind_speed_calculation_method === calculationMethods[2].value && (
              <Grid item xs={12} sm={12} style={{ position: 'relative' }} className="common_fullwidth">
                <FormControl
                  error={Boolean(errors && errors?.wind_speed_calculation_table_id)}
                  className="common_fullwidth"
                >
                  <InputLabel style={{ backgroundColor: 'white', padding: '0 5px' }}>
                    {t('flowActions.tasks.panels.parameters.operational.wind_speed_calculation_table_id.label')}
                  </InputLabel>
                  <Select
                    className="common_fullwidth"
                    value={wind_speed_calculation_table_id}
                    onChange={onChangeWindSpeedCalculationTableId}
                    variant={variantTypes.OUTLINED}
                  >
                    {alphaFactors.wind.map((_wind, index) => (
                      <MenuItem key={`windFactor-${index}`} value={_wind.id}>
                        {_wind.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{(errors && errors?.wind_speed_calculation_table_id) || ''}</FormHelperText>
                </FormControl>
              </Grid>
            )
          }

          {/*  current_speed_operational  /  current_speed_calculation_method  */}
          <Grid item xs={12} sm={12} style={{ position: 'relative' }}>
            <TextField
              disabled={current_speed_calculation_method === calculationMethodValues.AS_DESIGNED}
              type={textFieldTypes.NUMBER}
              name="current_speed_operational"
              id="current_speed_operational"
              value={current_speed_operational}
              variant={variantTypes.OUTLINED}
              label={t('flowActions.tasks.panels.parameters.operational.current_speed_operational.label')}
              className="common_halfwidth"
              InputLabelProps={{ shrink: true }}
              onChange={onChangeCurrentSpeedOperational}
              placeholder={t('flowActions.tasks.panels.parameters.operational.current_speed_operational.placeholder')}
              error={Boolean(errors && errors?.current_speed_operational)}
              helperText={(errors && errors?.current_speed_operational) || ''}
            />
            <FormControl
              error={Boolean(errors && errors?.current_speed_calculation_method)}
              style={{ marginLeft: '10px' }}
            >
              <InputLabel style={{ backgroundColor: 'white', padding: '0 5px' }}>
                {t('flowActions.tasks.panels.parameters.operational.current_speed_calculation_method.label')}
              </InputLabel>
              <Select
                style={{ width: UNIT_MENU_WIDTH }}
                value={current_speed_calculation_method}
                defaultValue={defaultCurrentSpeedCalculationMethod}
                onChange={onChangeCurrentSpeedCalculationMethod}
                variant={variantTypes.OUTLINED}
              >
                {calculationMethods
                  .filter((el) => el.mandatory === true)
                  .map((calcul, index) => (
                    <MenuItem key={`currentSpeed-calculationMethod-${index}`} value={calcul.value}>
                      {t(
                        `flowActions.tasks.panels.parameters.operational.calculation_method.${String(
                          calcul.id
                        ).toLocaleLowerCase()}`
                      )}
                    </MenuItem>
                  ))}
              </Select>
              <FormHelperText>{(errors && errors?.current_speed_calculation_method) || ''}</FormHelperText>
            </FormControl>
          </Grid>

          {/*  custom_criteria_operational  /  custom_criteria_calculation_method  */}
          <Grid item xs={12} sm={12} style={{ position: 'relative' }}>
            <TextField
              disabled={custom_criteria_calculation_method === calculationMethodValues.AS_DESIGNED}
              type={textFieldTypes.NUMBER}
              name="custom_criteria_operational"
              id="custom_criteria_operational"
              value={custom_criteria_operational}
              variant={variantTypes.OUTLINED}
              label={t('flowActions.tasks.panels.parameters.operational.custom_criteria_operational.label')}
              className="common_halfwidth"
              InputLabelProps={{ shrink: true }}
              onChange={onChangeCustomCriteriaOperational}
              placeholder={t('flowActions.tasks.panels.parameters.operational.custom_criteria_operational.placeholder')}
              error={Boolean(errors && errors?.custom_criteria_operational)}
              helperText={(errors && errors?.custom_criteria_operational) || ''}
            />
            <FormControl
              error={Boolean(errors && errors?.custom_criteria_calculation_method)}
              style={{ marginLeft: '10px' }}
            >
              <InputLabel style={{ backgroundColor: 'white', padding: '0 5px' }}>
                {t('flowActions.tasks.panels.parameters.operational.custom_criteria_calculation_method.label')}
              </InputLabel>
              <Select
                style={{ width: UNIT_MENU_WIDTH }}
                value={custom_criteria_calculation_method}
                defaultValue={defaultCustomCriteriaCalculationMethod}
                onChange={onChangeCustomCriteriaCalculationMethod}
                variant={variantTypes.OUTLINED}
              >
                {calculationMethods
                  .filter((el) => el.mandatory === true)
                  .map((calcul, index) => (
                    <MenuItem key={`currentSpeed-calculationMethod-${index}`} value={calcul.value}>
                      {t(
                        `flowActions.tasks.panels.parameters.operational.calculation_method.${String(
                          calcul.id
                        ).toLocaleLowerCase()}`
                      )}
                    </MenuItem>
                  ))}
              </Select>
              <FormHelperText>{(errors && errors?.custom_criteria_calculation_method) || ''}</FormHelperText>
            </FormControl>
          </Grid>
        </Grid>
        <FlowElementBottomButtons onSave={onClickSave} />
      </form>
    </div>
  )
}

FlowElementTaskParameterPanel.displayName = 'FlowElementTaskParameterPanel'

FlowElementTaskParameterPanel.propTypes = {
  currentTaskInstance: PropTypes.object.isRequired,
  alphaFactors: PropTypes.object,
  weather_data: PropTypes.arrayOf(PropTypes.object),
  onChangeTask: PropTypes.func.isRequired,
  onSaveTask: PropTypes.func.isRequired,
  errors: PropTypes.array
}

FlowElementTaskParameterPanel.defaultProps = {
  currentTaskInstance: null,
  alphaFactors: [],
  weather_data: [],
  onChangeTask: () => {},
  onSaveTask: () => {},
  errors: null
}
